import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-upload-how',
    category: 'data',
    title: 'Eine Datei hochladen – Schritt für Schritt',
    summary: 'So laden Sie eine CSV-Datei hoch und lesen das Validierungsergebnis.',
    body: (
      <>
        <p>Sobald Ihre CSV-Datei fertig ist, laden Sie sie innerhalb der Gemeinschaft hoch:</p>
        <ol>
          <li>Öffnen Sie Ihre Gemeinschaft und wählen Sie <strong>Datenverwaltung</strong> im linken Menü.</li>
          <li>Wählen Sie unter <strong>Datensatztyp auswählen</strong> die Art der Datei, die Sie hochladen
            (zum Beispiel „Building Load Profile").</li>
          <li>Falls gefragt, wählen Sie, zu welchem <strong>Gebäude</strong> die Datei gehört.</li>
          <li>Ziehen Sie Ihre CSV-Datei auf den Upload-Bereich oder klicken Sie auf <strong>Dateien durchsuchen</strong>.</li>
          <li>Klicken Sie auf <strong>Validieren &amp; Vorschau</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="upload-page.png"
          description="The community Data page with a dataset type selected, showing the required-columns badges, the download-template button, and the drag-and-drop upload area."
          source="Platform — open any community → click 'Data Management' in the sidebar → pick a dataset type from the dropdown so the upload area appears. Capture the form area."
          caption="Die Datenverwaltungsseite: Typ auswählen, dann Datei ablegen."
        />
        <h5 className="mt-4">Das Ergebnis lesen</h5>
        <ul>
          <li><strong>Grünes Banner</strong> — die Datei ist gültig. Es zeigt, wie viele Zeilen gelesen wurden und den Datumsbereich. Sie sind fertig.</li>
          <li><strong>Gelbe Warnungen</strong> — die Datei wurde akzeptiert, aber etwas sollte überprüft werden (zum Beispiel fehlende Stunden). Sie können sie behalten oder die Datei verbessern.</li>
          <li><strong>Rotes Banner</strong> — die Datei wurde abgelehnt. Die Vorschau listet jedes Problem mit seiner Zeilennummer auf. Korrigieren Sie die Datei und laden Sie sie erneut hoch.</li>
        </ul>
        <ScreenshotPlaceholder
          img="upload-validation-result.png"
          description="The validation result panel after a successful upload — green success banner plus the data preview table."
          source="Platform — on the same Data Management page, after uploading a valid CSV and clicking 'Validate & Preview'. Capture the green banner and the first few rows of the preview table."
          caption="Ein erfolgreicher Upload zeigt ein grünes Banner und eine Vorschau."
        />
        <p>
          Jede rote Fehlermeldung wird in
          <em> „Warum ist mein Upload fehlgeschlagen?"</em> im Abschnitt Fehlerbehebung erklärt.
        </p>
      </>
    ),
  },

  {
    id: 'data-load',
    category: 'data',
    title: 'Verbrauchsdateien für Gebäude (Last)',
    summary: 'Die stündliche Stromverbrauchsdatei – die wichtigste Datei für jedes Gebäude.',
    body: (
      <>
        <p>
          Ein <strong>Building Load Profile</strong> erfasst, wie viel Strom ein Gebäude verbraucht hat,
          ein Wert pro Stunde. Es ist die Grundlage für Prognose und Optimierung – erstellen Sie
          daher möglichst für jedes Gebäude eines.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Spalte</th><th>Erforderlich?</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Erforderlich</td><td>Die Stunde, z. B. <Code>2025-01-01T08:00:00Z</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Erforderlich</td><td>In dieser Stunde verbrauchter Strom in kWh (eine Zahl, Punkt als Dezimalzeichen, niemals negativ)</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Optional</td><td>Höchste Leistungsaufnahme in dieser Stunde, in kW</td></tr>
          </tbody>
        </Table>
        <p><strong>Beispiel:</strong></p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,consumption_kwh
2025-01-01T00:00:00Z,0.457
2025-01-01T01:00:00Z,0.343
2025-01-01T02:00:00Z,0.298`}
        </pre>
        <p>Ein Wert pro Stunde; ein volles Jahr umfasst 8.760 Zeilen. Mindestens 24 Zeilen (ein Tag) sind erforderlich.</p>
        <TemplateBtn type="building_load_profile">Lastprofil-Vorlage herunterladen</TemplateBtn>
        <Warn>Der Verbrauch kann niemals negativ sein. Ein negativer Wert bedeutet, dass eine Spalte verwechselt wurde – prüfen Sie, ob die Erzeugung nicht in der Verbrauchsspalte eingetragen wurde.</Warn>
      </>
    ),
  },

  {
    id: 'data-generation',
    category: 'data',
    title: 'Solar- und Winderzeugungs­dateien',
    summary: 'Stündliche Leistung von Solaranlagen und Windturbinen.',
    body: (
      <>
        <p>Erzeugungsdateien erfassen, wie viel Energie eine erneuerbare Quelle produziert hat, ein Wert pro Stunde.</p>
        <h5 className="mt-3">Gemeinschaftliche Solaranlage oder Windturbine</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Spalte</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Die Stunde, z. B. <Code>2025-01-01T12:00:00Z</Code></td></tr>
            <tr><td><Code>generation_kw</Code></td><td>In dieser Stunde erzeugte Leistung in kW (niemals negativ – nachts ist Solar <Code>0</Code>)</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,generation_kw
2025-01-01T00:00:00Z,0
2025-01-01T12:00:00Z,105.6
2025-01-01T18:00:00Z,12.3`}
        </pre>
        <TemplateBtn type="pv_generation_profile">Solar (PV) template</TemplateBtn>
        <TemplateBtn type="wind_generation_profile">Wind template</TemplateBtn>

        <h5 className="mt-4">Hauseigene Dach-PV eines Gebäudes</h5>
        <p>
          Bei Solar, das einem einzelnen Gebäude gehört, lautet die Wertspalte
          <Code>generation_kwh</Code> (Energie in kWh) statt <Code>generation_kw</Code>.
          Die Plattform zeigt den erwarteten Spaltennamen genau an, wenn Sie den Datensatztyp auf der
          Upload-Seite auswählen – folgen Sie stets den dortigen Angaben.
        </p>
        <Tip>Stunden ohne Sonne oder Wind sollten <Code>0</Code> enthalten und nicht leer gelassen werden.</Tip>
      </>
    ),
  },

  {
    id: 'data-tariff',
    category: 'data',
    title: 'Tariffdateien (Strompreise)',
    summary: 'Import- und Exportstrompreise – historisch und Day-Ahead.',
    body: (
      <>
        <p>Tariffdateien teilen der Plattform den Strompreis für jede Stunde mit.</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Datei</th><th>Wertspalte</th><th>Zeilen</th></tr>
          </thead>
          <tbody>
            <tr><td>Historischer Importtariff</td><td><Code>import_price_eur_kwh</Code></td><td>Ein Jahr, stündlich (≈8.760)</td></tr>
            <tr><td>Historischer Exporttariff</td><td><Code>export_price_eur_kwh</Code></td><td>Ein Jahr, stündlich (≈8.760)</td></tr>
            <tr><td>Day-Ahead Importtariff</td><td><Code>import_price_eur_kwh</Code></td><td>24 (ein Tag)</td></tr>
            <tr><td>Day-Ahead Exporttariff</td><td><Code>export_price_eur_kwh</Code></td><td>24 (ein Tag)</td></tr>
          </tbody>
        </Table>
        <p>
          Der <strong>Import</strong>preis ist das, was Sie für den Kauf von Strom zahlen; der <strong>Export</strong>preis
          ist das, was Sie für die Einspeisung erhalten. Preise sind in Euro pro kWh (z. B. <Code>0.22</Code>)
          und verwenden einen Punkt als Dezimalzeichen.
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,import_price_eur_kwh
2025-01-01T00:00:00Z,0.08
2025-01-01T12:00:00Z,0.22
2025-01-01T19:00:00Z,0.28`}
        </pre>
        <TemplateBtn type="energy_tariff_data">Importtariff-Vorlage</TemplateBtn>
        <TemplateBtn type="feed_in_tariff_data">Exporttariff-Vorlage</TemplateBtn>
        <Tip>
          <strong>Day-ahead</strong>-Dateien umfassen nur einen Tag (24 Zeilen). Laden Sie täglich eine neue hoch –
          die Zeitstempel sollten mit dem Datum dieses Tages übereinstimmen. Die obigen Vorlagen dienen
          auch als Formatreferenz für 24 Zeilen.
        </Tip>

        <h5 className="mt-4">Wo bekomme ich Tariffdaten?</h5>
        <p>
          Zwei verschiedene Dinge werden „Tariff" genannt – behandeln Sie sie getrennt:
        </p>
        <ul>
          <li><strong>Großhandels- / Day-Ahead-Preise</strong> — der stündliche Marktpreis, der von der
            Strombörse Ihres Landes veröffentlicht wird. Verwenden Sie diesen für <em>Day-Ahead</em>-Tariffdateien
            und als gute Annäherung für variable Verträge.</li>
          <li><strong>Einzelhandelstariff</strong> — was Sie tatsächlich zahlen (Energie + Netzentgelte + Steuern).
            Entnehmen Sie diesen dem Vertrag oder der Rechnung Ihres Versorgers.</li>
        </ul>

        <p className="mt-3"><strong>Gesamteuropäische Quelle (für jedes Land geeignet):</strong></p>
        <ul>
          <li><strong>ENTSO-E Transparency Platform</strong> —
            {' '}<Ext href="https://transparency.entsoe.eu">transparency.entsoe.eu</Ext>. Kostenlos, erfordert
            jedoch ein kostenloses Konto. Öffnen Sie <em>Market → Day-ahead Prices</em>, wählen Sie Ihre
            Gebotszone (z. B. IT-North, FR, DE-LU, CZ, GR), den Datumsbereich und <em>Export Data</em> → CSV.
            Die Preisspalte ist in <strong>€/MWh – durch 1000 teilen, um in €/kWh umzurechnen</strong>
            (z. B. 220 €/MWh = 0,22 €/kWh).</li>
        </ul>

        <p className="mt-3"><strong>Nationale Quellen (oft einfacher, kein Konto erforderlich):</strong></p>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Land</th><th>Website</th><th>Was Sie erhalten</th></tr>
          </thead>
          <tbody>
            <tr><td>Italien</td><td><Ext href="https://www.mercatoelettrico.org">mercatoelettrico.org</Ext> (GME)</td><td>PUN Day-Ahead-Preis, stündlich. Suchen Sie nach „Esiti MGP". €/MWh – durch 1000 teilen.</td></tr>
            <tr><td>Frankreich</td><td><Ext href="https://data.rte-france.com">data.rte-france.com</Ext> (RTE Open Data) oder <Ext href="https://www.epexspot.com/en">epexspot.com</Ext></td><td>RTE: kostenloses Open-Data-Portal, stündliche Day-Ahead-Preise. EPEX: Marktseite.</td></tr>
            <tr><td>Deutschland</td><td><Ext href="https://www.smard.de/en">smard.de</Ext> (Bundesnetzagentur)</td><td>„Marktdaten" → „Großhandelspreise" → „Day-Ahead, Deutschland/Luxemburg". Kostenloser Download, kein Konto erforderlich.</td></tr>
            <tr><td>Tschech. Rep.</td><td><Ext href="https://www.ote-cr.cz/en">ote-cr.cz</Ext> (OTE)</td><td>„Short-term markets" → „Day-ahead market". CSV-Export pro Tag.</td></tr>
            <tr><td>Griechenland</td><td><Ext href="https://www.enexgroup.gr/web/guest/markets-publications-el-day-ahead-market">enexgroup.gr</Ext> (HEnEx)</td><td>Day-Ahead-Marktergebnisse, tägliche PDFs und CSVs. €/MWh – durch 1000 teilen.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Eine heruntergeladene Preisdatei in das Plattformformat umwandeln</h5>
        <p>
          Strombörsen-Exporte kommen selten mit den genauen Spaltennamen, die die Plattform erwartet. Führen
          Sie diese Schritte durch, um eine der obigen Dateien zu konvertieren:
        </p>
        <ol>
          <li>Öffnen Sie die heruntergeladene CSV in Excel oder Google Sheets.</li>
          <li>Identifizieren Sie die Datum/Uhrzeit-Spalte und die Preisspalte (andere Spalten können gelöscht werden).</li>
          <li>Erstellen Sie eine Spalte namens <Code>timestamp</Code>. Wenn die Quelle bereits einen ISO-Zeitstempel
            enthält, kopieren Sie ihn; andernfalls erstellen Sie einen mit der Formel aus
            {' '}<ArticleLink to="data-csv-basics">CSV-Dateien korrekt erstellen</ArticleLink>.</li>
          <li>Erstellen Sie eine Spalte namens <Code>import_price_eur_kwh</Code>. Wenn die Quelle in €/MWh angegeben
            ist, teilen Sie durch 1000 mit einer Formel wie <Code>=B2/1000</Code> und fügen Sie als Werte ein.</li>
          <li>Speichern Sie als <strong>CSV UTF-8</strong> und laden Sie als <em>Historischer Importtariff</em> hoch.</li>
        </ol>

        <Warn>
          Großhandelspreis ≠ Einzelhandelspreis. Der Großhandelspreis ist nur der Energiemarktpreis und
          enthält keine Netzentgelte, Abgaben oder Mehrwertsteuer. Für eine genaue Kostenanalyse bevorzugen
          Sie einen Einzelhandelstariff Ihres Versorgers oder kombinieren Sie den Großhandelspreis mit einem
          bekannten festen Aufschlag.
        </Warn>

        <h5 className="mt-4">Exporttariff (Einspeisevergütung)</h5>
        <p>
          Exportpreise hängen von Ihrem nationalen System ab – sie können eine feste Einspeisevergütung,
          den Großhandelspreis abzüglich eines Abschlags oder null sein. Entnehmen Sie die Regelung Ihrem
          Versorger, Ihrem DSO oder Ihrer nationalen Regulierungsbehörde (ARERA in Italien, CRE in Frankreich,
          BNetzA in Deutschland, ERÚ in Tschechien, RAE in Griechenland) und tragen Sie den resultierenden
          €/kWh-Wert für jede Stunde ein.
        </p>
      </>
    ),
  },
]
